<template>
  <div>
    <div class="c-search-bar">
      <div class="c-search-bar__inner">
        <input
          id="keywordInput"
          ref="keywordInput"
          class="c-search-input"
          placeholder="Enter search terms…"
          aria-label="Search"
          :value="queryData.keyword"
          @input="updateKeyword"
          @change="(event) => (queryData.keyword = event.target.value)"
        />
        <button
          type="button"
          class="c-search-clear"
          @click="clearSearch()"
          aria-label="Search Clear Button"
        ></button>
        <div v-if="items" class="vl"></div>
        <button
          v-if="items"
          type="button"
          class="c-search-filters"
          @click="toggleFilters"
          aria-label="Search Filters Button"
        ></button>
      </div>
    </div>
    <div class="container" v-if="showListing">
      <div class="row">
        <!-- Left Sidebar -->
        <div class="c-search-sidebar col-md-3 col-sm-12" :class="{ show: showFilterMobile }">
          <div class="c-search-sidebar__header">
            <div class="c-search-sidebar__header--mobile">
              <h1>Filters</h1>
              <button
                type="button"
                class="c-search-filters-close"
                @click="toggleFilters"
                aria-label="Close Filters Button"
              ></button>
            </div>
            <div class="c-search-sidebar__header--sub">
              <span class="title">Filter By</span>
              <span class="totals"
                ><strong>{{ totalCount }}</strong> Results Found</span
              >
              <a @click="queryData.clearFilters()">Clear Filters</a>
            </div>
          </div>
          <div class="c-search-sidebar__filters">
            <h6>Type</h6>
            <ul class="c-search-type" v-if="filters.types">
              <li v-for="item in filters.types" :key="item.id">
                <label class="c-checkbox" :for="item.name">
                  <input
                    type="checkbox"
                    name="types"
                    :id="item.name"
                    :value="item.shortName"
                    :checked="isTypeSelected(item.shortName)"
                    @change="toggleType"
                  />
                  {{ item.displayName }}
                </label>
              </li>
            </ul>
            <hr />
            <h6>Topic</h6>
            <ul class="c-search-topic" v-if="filters.topics">
              <li v-for="topic in filters.topics" :key="topic.id">
                <label class="c-checkbox" :for="topic.name">
                  <input
                    type="checkbox"
                    name="topics"
                    :id="topic.name"
                    :value="topic.shortName"
                    :checked="isTopicSelected(topic.shortName)"
                    @change="toggleTopic"
                  />
                  {{ topic.displayName }}
                </label>
              </li>
            </ul>
          </div>
        </div>
        <!-- Main Content -->
        <div class="c-search-list col-md-9 col-sm-12">
          <p v-if="loading">Searching ...</p>
          <div v-else-if="items">
            <p v-if="error" class="alert alert-danger">
              Search query error: {{ error.toString() }}
            </p>

            <p v-if="totalPages === 0">
              Showing 0 results for "<strong>{{ searchedKeyword }}</strong
              >"
            </p>
            <p v-else-if="totalPages > 1">
              Showing results {{ startItemIndex }} - {{ endItemIndex }} of {{ totalCount }} for
              "<strong>{{ searchedKeyword }}</strong
              >"
            </p>
            <p v-else-if="endItemIndex == 1">
              Showing 1 result for "<strong>{{ searchedKeyword }}</strong
              >"
            </p>
            <p v-else>
              Showing results {{ startItemIndex }} - {{ totalCount }} for "<strong>{{
                searchedKeyword
              }}</strong
              >"
            </p>
            <div>
              <div v-for="item in items" :key="item.id" class="c-search-list-item">
                <div v-if="item.item && item.item.url" class="c-search-list-item__img">
                  <a :href="item.item.url">
                    <sc-image :media="getImage(item)" />
                  </a>
                </div>
                <div v-if="item.item && item.item.url" class="c-search-list-item__content">
                  <h6>
                    <strong v-if="item.templateName">{{
                      getTemplateName(item.templateName)
                    }}</strong>
                    <a
                      v-if="
                        item.item.url &&
                        ((item.item.url.startsWith('http') && item.item.url.length > 91) ||
                          (item.item.url.startsWith('/') && item.item.url.length > 65))
                      "
                      :href="item.item.url"
                      class="c-search-list-item__link"
                    >
                      -
                      {{
                        (
                          (item.item.url.startsWith('http') ? '' : 'https://www.cisecurity.org') +
                          item.item.url
                        ).substr(0, parseInt(91)) + '...'
                      }}
                    </a>
                    <a v-else :href="item.item.url" class="c-search-list-item__link">
                      -
                      {{
                        (item.item.url.startsWith('http') ? '' : 'https://www.cisecurity.org') +
                        item.item.url
                      }}
                    </a>
                  </h6>
                  <h2>
                    <a v-if="item.item.url" :href="item.item.url">{{ item.item.displayName }}</a>
                  </h2>
                  <p v-if="item.resourceExcerpt">
                    {{ item.resourceExcerpt.trim().substr(0, parseInt(150)) + '...' }}
                  </p>
                  <p v-else-if="item.pageDescription">
                    {{
                      item.pageDescription
                        .substr(0, parseInt(150))
                        .replace(/(&lt;([^>]+)>)/gi, '') + '...'
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p v-else>Please enter search term longer than {{ minKeywordLength }} characters.</p>
        </div>
      </div>
      <div class="row" v-if="!loading">
        <nav aria-label="Search Paging" v-if="totalPages > 1">
          <ul class="pagination">
            <li class="page-item">
              <button class="page-link" :class="{ disabled: isInFirstPage }" @click="previousPage">
                &lsaquo; prev
              </button>
            </li>
            <li
              v-for="(page, index) in pages"
              :key="`${index}`"
              class="page-item"
              :class="{ active: isPageActive(page) }"
            >
              <button
                class="page-link"
                :class="{ disabled: page === '...' }"
                @click="getPage(page)"
              >
                {{ page }}
              </button>
            </li>
            <li class="page-item">
              <button class="page-link" :class="{ disabled: isInLastPage }" @click="nextPage">
                next &rsaquo;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance, reactive, shallowRef } from 'vue';
import { SearchQuery } from './CIS-Search.graphql';
import { useQuery } from '@vue/apollo-composable';
import { Image } from '@sitecore-jss/sitecore-jss-vue';
import _debounce from 'lodash/debounce';
import config from '../../../temp/config';

export default {
  name: 'CIS-Search',
  components: {
    ScImage: Image
  },
  props: {
    fields: {
      type: Object
    },
    rendering: {
      type: Object
    }
  },
  setup(props) {
    const instance = getCurrentInstance();
    const route = instance?.appContext?.config?.globalProperties?.$route;

    const queryData = reactive({
      keyword: route?.query?.s || '',
      page: parseInt(route?.query?.page) || 1,
      pageSize: props?.fields?.pageSize.value || 10,
      selectedTypes: [].concat(route?.query?.type || []),
      selectedTopics: [].concat(route?.query?.topic || []),
      clearSearch: function () {
        this.page = 1;
        this.keyword = '';
        this.clearFilters();
      },
      clearFilters: function () {
        this.selectedTopics = [];
        this.selectedTypes = [];
      }
    });

    const filters = {
      topics: props.fields?.topics?.map((t) => {
        return {
          ...t,
          shortName: t.name.replace('-owner', '').toLowerCase()
        };
      }),
      types: props.fields?.types?.map((t) => {
        return {
          ...t,
          shortName: t.name.replace('CIS-Resource-', '').replace('-RouteType', '').toLowerCase()
        };
      })
    };

    let historyPush = true;
    const variables = () => {
      const fieldsEqual = [];
      if (queryData.selectedTypes.length > 0) {
        fieldsEqual.push({
          name: '_templatename',
          value: filters.types
            .filter((t) => {
              return queryData.selectedTypes.includes(t.shortName);
            })
            .map((t) => {
              return t.name;
            })
            .join(',')
        });
      }

      if (queryData.selectedTopics.length > 0) {
        fieldsEqual.push({
          name: 'owners',
          value: filters.topics
            .filter((t) => {
              return queryData.selectedTopics.includes(t.shortName);
            })
            .map((t) => {
              return t.id.replaceAll('-', '').toUpperCase();
            })
            .join(',')
        });
      }

      if (queryData.page < 1) queryData.page = 1;

      return {
        keyword: queryData.keyword,
        pageSize: queryData.pageSize,
        after: Math.max(0, (queryData.page - 1) * queryData.pageSize).toString(),
        fieldsEqual: fieldsEqual
      };
    };

    const minKeywordLength = 2;
    const queryOptions = () => {
      return {
        enabled: queryData.keyword && queryData.keyword.length > minKeywordLength,
        prefetch: true
      };
    };

    const { result, loading, error, onResult } = useQuery(SearchQuery, variables, queryOptions);

    // Seperate ref variable to track the keyword used in an actual query.
    // - The 'queryData.keyword' property can change with user input, but the query
    //   might not be executed based on the 'options.enabled' property.
    const searchedKeyword = shallowRef(null);

    onResult((queryResult) => {
      if (!queryResult.loading && queryResult.data) {
        searchedKeyword.value = queryData.keyword;
        showListing.value = true;

        // Avoid if window object doesn't exist, i.e. Server-Side Rendering
        //  - Rewriting the URL isn't needed in SSR because the URL is from a
        //    standard browser GET and not an update to the Vue component.
        if (typeof window === 'object' && window.location) {
          // Update URL to track query data changes
          const url = new URL(window.location);
          url.searchParams.set('s', queryData.keyword);
          url.searchParams.set('page', queryData.page);

          url.searchParams.delete('topic');
          queryData.selectedTopics.forEach((t) => url.searchParams.append('topic', t));
          url.searchParams.delete('type');
          queryData.selectedTypes.forEach((t) => url.searchParams.append('type', t));

          url.searchParams.sort();
          // Only push the state if the search string is different from current URL.
          // - This happens during browser 'back' navigation.
          if (window.location.search !== url.search) {
            // Sometimes replaceState is necessary instead of pushState to prevent
            // the browser's back button from navigating to an invalid page.
            if (historyPush) {
              window.history.pushState({}, 'Search', url);
            } else {
              window.history.replaceState({}, 'Search', url);
            }
          }
        }

        historyPush = true;
        // Reset 'page' property when out of range (ex. /search?page=99999&s=virus)
        if (
          queryResult.data?.search?.results?.totalCount > 0 &&
          queryResult.data?.search?.results?.pageInfo?.startCursor === '0' &&
          queryResult.data?.search?.results?.pageInfo?.endCursor === '0'
        ) {
          // Changing the page property will cause the query to re-run.
          queryData.page =
            Math.ceil(queryResult.data?.search?.results?.totalCount / queryData.pageSize) || 1;
          // When this state is pushed into the browser's history it should use
          // the replaceState function so the browser's back button doesn't navigate
          // back to this out of range page.
          historyPush = false;
        }
      }
    });

    // Controls if the listing container element is rendered. When
    // first loading the page without any parameters, or when user clears
    // the search, set to false.
    const showListing = shallowRef(false);

    // Ref to the keywordInput textbox so it can be manipulated (ex. set focus).
    const keywordInput = shallowRef(null);

    // Controls if filter panel is visible in mobile view
    const showFilterMobile = shallowRef(false);

    return {
      queryData,
      queryOptions,
      minKeywordLength,
      searchedKeyword,
      keywordInput,
      showListing,
      showFilterMobile,
      filters,
      historyPush,
      result,
      loading,
      error
    };
  },
  computed: {
    items() {
      return !this.loading && this.queryOptions()?.enabled && this.result?.search?.results?.items;
    },
    startItemIndex() {
      return (this.queryData.page - 1) * this.queryData.pageSize + 1;
    },
    endItemIndex() {
      return Math.min(this.queryData.page * this.queryData.pageSize, this.totalCount);
    },
    totalCount() {
      return this.result?.search?.results ? this?.result?.search?.results?.totalCount ?? 0 : null;
    },
    isInFirstPage() {
      return this.queryData.page === 1;
    },
    isInLastPage() {
      return this.queryData.page === this.totalPages;
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.queryData.pageSize);
    },
    pages() {
      const pagination = (currentPage, pageCount) => {
        const delta = 1,
          left = currentPage - delta,
          right = currentPage + delta + 1;

        const result = Array.from({ length: pageCount }, (v, k) => k + 1).filter(
          (i) => i && i >= left && i < right
        );

        if (result.length > 1) {
          // Add first page and dots
          if (result[0] > 1) {
            if (result[0] > 2) {
              result.unshift('...');
            }
            result.unshift(1);
          }

          // Add dots and last page
          if (result[result.length - 1] < pageCount) {
            if (result[result.length - 1] !== pageCount - 1) {
              result.push('...');
            }
            result.push(pageCount);
          }
        }

        return result;
      };

      return pagination(this.queryData.page, this.totalPages);
    }
  },
  methods: {
    updateKeyword: _debounce(function (e) {
      this.queryData.keyword = e.target.value;
      this.queryData.page = 1;
    }, 700),
    toggleTopic(e) {
      if (this.queryData.selectedTopics.includes(e.target.value)) {
        this.queryData.selectedTopics = this.queryData.selectedTopics.filter((t) => {
          return t !== e.target.value;
        });
      } else {
        this.queryData.selectedTopics.push(e.target.value);
      }
      this.queryData.page = 1;
    },
    isTopicSelected(name) {
      return this.queryData.selectedTopics.includes(name);
    },
    toggleType(e) {
      if (this.queryData.selectedTypes.includes(e.target.value)) {
        this.queryData.selectedTypes = this.queryData.selectedTypes.filter((t) => {
          return t !== e.target.value;
        });
      } else {
        this.queryData.selectedTypes.push(e.target.value);
      }
      this.queryData.page = 1;
    },
    isTypeSelected(name) {
      return this.queryData.selectedTypes.includes(name);
    },
    getTemplateName(template) {
      const templateName =
        this.fields?.types?.find((t) => t.name === template)?.displayName ?? 'Page';

      return templateName.replace('Resource', '').toUpperCase();
    },
    getImage(item) {
      if (item?.item?.fImage?.jss?.value?.src) {
        return item?.item?.fImage?.jss;
      } else if (item?.templateName === 'CIS-Resource-Benchmark-RouteType') {
        return {
          src: `${config.sitecoreApiHost}/-/media/Project/cisecurity/cisecurity/data/media/img/thumbnail-cis-benchmarks.png`,
          alt: 'CIS Benchmarks',
          width: '250',
          height: '160'
        };
      } else if (item?.templateName !== 'CIS-Resource-Advisory-RouteType') {
        return this.getOwnerImage(item?.owners);
      } else {
        return {
          src: `${config.sitecoreApiHost}/-/media/Project/cisecurity/cisecurity/data/media/img/thumbnail-cis.png`,
          alt: 'CIS',
          width: '250',
          height: '160'
        };
      }
    },
    getOwnerImage(owners) {
      const ownersList = owners?.split('|') || [];
      const ownerName =
        this.fields.topics?.find((t) => ownersList.includes(t.id.replaceAll('-', '').toLowerCase()))
          ?.name ?? 'CIS';

      let image = '';
      if (ownerName === 'cis-benchmarks-owner') {
        image = 'thumbnail-cis-benchmarks';
      } else if (ownerName === 'cis-controls-owner') {
        image = 'thumbnail-cis-controls';
      } else if (ownerName === 'cis-cybermarket-owner') {
        image = 'thumbnail-cis-cybermarket';
      } else if (ownerName === 'cis-hardened-images-owner') {
        image = 'thumbnail-cis-hardened-images';
      } else if (ownerName === 'cis-securesuite-owner') {
        image = 'thumbnail-cis-securesuite';
      } else if (ownerName === 'cis-services-owner') {
        image = 'thumbnail-cis-services';
      } else if (ownerName === 'eiisac-owner') {
        image = 'thumbnail-cis-ei-isac';
      } else if (ownerName === 'msisac-owner') {
        image = 'thumbnail-cis-ms-isac';
      } else {
        image = 'thumbnail-cis';
      }

      return {
        value: {
          src: `${config.sitecoreApiHost}/-/media/project/cisecurity/cisecurity/data/media/img/${image}`,
          alt: ownerName,
          width: '250',
          height: '160'
        }
      };
    },
    clearSearch() {
      this.queryData.clearSearch();
      const url = new URL(window.location);
      if (url.search?.length > 0) {
        url.search = '';
        window.history.pushState({}, 'Search', url);
      }
      this.showListing = false;
      this.keywordInput.focus();
    },
    toggleFilters() {
      this.showFilterMobile = !this.showFilterMobile;
    },
    scrollTop() {
      // In case the header banner is visible
      const bannerHeight =
        document.getElementsByClassName('c-banner__content')[0]?.offsetHeight || 0;
      // This should be the .c-search-bar element
      const top = (this.keywordInput.parentElement?.parentElement?.offsetTop || 100) - bannerHeight;
      window.scrollTo({
        top: top,
        left: 0,
        behavior: 'smooth'
      });
    },
    previousPage() {
      this.scrollTop();
      this.queryData.page--;
    },
    nextPage() {
      this.scrollTop();
      this.queryData.page++;
    },
    getPage(page) {
      this.scrollTop();
      this.queryData.page = page;
    },
    isPageActive(page) {
      return this.queryData.page === page;
    }
  }
};
</script>
<style lang="scss" scoped>
@import '~@/assets/scss/components/checkbox.scss';

.container {
  @include padding-x-thin;
  padding-bottom: 3rem;
}

.c-search-bar {
  padding: 2rem 1rem;
  color: $white;
  background: linear-gradient(
    169deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 59, 92, 1) 20%,
    rgba(0, 134, 191, 1) 100%
  );
  text-align: center;
  margin-bottom: 3rem;

  @include tablet-up {
    padding: 7rem 5rem;
  }

  &__inner {
    margin: 0 auto;
    display: flex;
    align-items: center;
    border-bottom: solid 2px rgba($white, 50%);
    padding-bottom: 1rem;
    column-gap: 2rem;
    max-width: 95%;
    @include padding-x-thin;

    .vl {
      border-right: 1px solid rgba(#fff, 75%);
      height: 4rem;

      @include desktop-up {
        display: none;
      }
    }
  }

  .c-search-input {
    width: 100%;
    height: 5rem;
    margin: 0 auto;
    padding: 0;
    padding-left: 3.5rem;
    box-sizing: border-box;
    border: none;
    background: url(~@/assets/images/icon-search.svg) transparent;
    background-position: 0% 50%;
    background-repeat: no-repeat;
    background-size: 2rem;
    outline: none;
    @include font-styles('Arial', 1.8rem, 100, normal, normal, 0, $white);

    @include desktop-up {
      font-size: 4rem;
      line-height: 4rem;
      height: 7rem;
      background-size: 3rem;
      padding-left: 4.5rem;
    }

    &::-webkit-input-placeholder {
      color: rgba($white, 50%);
    }
    &::-moz-placeholder {
      color: rgba($white, 50%);
    }
    &:-ms-input-placeholder {
      color: rgba($white, 50%);
    }
    &:-moz-placeholder {
      color: rgba($white, 50%);
    }
  }

  .c-search-clear,
  .c-search-filters {
    width: 2rem;
    height: 2rem;
    background-repeat: no-repeat;
    background-position: center;
    background-size: auto;
    background-color: transparent;
    border: none;
    cursor: pointer;

    @include desktop-up {
      margin: 0;
      width: 3rem;
      height: 3rem;
    }
  }

  .c-search-clear {
    background-image: url(~@/assets/images/icon-close-white.svg);
  }

  .c-search-filters {
    background-image: url(~@/assets/images/icon-filterbytopic.svg);

    @include desktop-up {
      display: none;
    }
  }
}

.c-search-sidebar {
  @include desktop-down {
    display: none;
    background-color: #fff;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    width: 100%;
    height: 100%;
    overflow: auto;
    padding-left: 0;
    padding-right: 0;

    & .show {
      display: block;
    }
  }

  @include desktop-up {
    display: block !important;
  }

  &__header {
    margin-bottom: 2rem;

    &--mobile {
      min-height: 7rem;
      display: flex;
      align-items: center;
      background: rgb(0, 123, 176);
      background: linear-gradient(180deg, rgba(0, 123, 176, 1) 0%, rgba(0, 73, 110, 1) 100%);
      margin-bottom: 2rem;

      @include desktop-up {
        display: none;
      }

      h1 {
        @include font-styles('Arial', 2rem, 300, normal, normal, 0, #fff);
        flex: 1 0 0%;
        background-clip: unset;
        -webkit-background-clip: unset;
        -webkit-text-fill-color: unset;
        background-repeat: no-repeat;
        background-position: left center;
        background-size: auto;
        background-color: transparent;
        background-image: url(~@/assets/images/icon-filterbytopic.svg);
        padding-left: 5rem;
        margin: 0;
        margin-left: 2rem;
      }

      .c-search-filters-close {
        text-align: right;
        flex: 0 0 10%;
        width: 4rem;
        height: 4rem;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 2rem;
        background-color: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
        margin: 0;

        @include desktop-up {
          margin: 0;
          width: 3rem;
          height: 3rem;
        }
      }

      .c-search-clear,
      .c-search-filters-close {
        background-image: url(~@/assets/images/icon-close-white.svg);
      }
    }

    &--sub {
      display: flex;
      align-items: center;
      margin-left: 2rem;
      margin-right: 2rem;
      padding-bottom: 2rem;
      border-bottom: 1px solid #dfdfdf;

      @include desktop-up {
        margin: 0;
      }

      .title,
      .totals {
        flex: 1 0 0%;
        color: $blue;
        font-weight: 400;
      }

      .title {
        display: none;

        @include desktop-up {
          display: inline;
        }
      }

      .totals {
        display: inline;
        color: $dark-gray;

        @include desktop-up {
          display: none;
        }
      }

      a {
        flex: 1 0 0%;
        text-align: right;
        @include font-styles('Arial', 2rem, 400, normal, 3rem, 0, $lite-gray);

        &:hover,
        &:active {
          color: $gray;
        }
      }
    }
  }

  &__filters {
    margin-left: 2rem;
    margin-right: 2rem;

    @include desktop-up {
      background-color: #f3f4f4;
      margin: 0;
      padding: 2rem;
    }

    h6 {
      text-transform: uppercase;
      margin: 0;
      margin-bottom: 2rem;
      font-size: 1.4rem;
      font-weight: 600;
    }

    ul {
      margin: 0;

      li {
        margin: 0;

        &:before {
          display: none;
        }

        &::marker {
          content: '';
          color: transparent;
        }

        label {
          @include font-styles('Arial', 1.8rem, 200, normal, 2.4rem, 0, $dark-gray);
          margin: 0;
          margin-bottom: 1rem;
          cursor: pointer;

          input {
            cursor: pointer;
          }
        }
      }
    }

    hr {
      border-color: #dbdcdc;
      margin: 2rem 0 2.5rem 0;
    }
  }
}

.c-search-list {
  @include desktop-up {
    padding-top: 0.5rem;
    padding-right: 10rem;
  }

  p {
    @include font-styles('Arial', 1.8rem, 200, normal, 2.4rem, 0, $gray);
    margin-bottom: 2rem;

    strong {
      @include font-styles('Arial', 1.8rem, 500, normal, 2.4rem, 0, $gray);
    }
  }

  &-item {
    display: flex;
    flex-direction: row;
    column-gap: 1rem;
    margin-bottom: 3rem;
    border-bottom: 1px solid #dcdcdc;
    padding-bottom: 2.5rem;

    @include tablet-up {
      column-gap: 3rem;
    }

    @include desktop-up {
      padding-bottom: 0;
      border-bottom: 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      margin: 0;
    }

    &__img {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      width: auto;
      line-height: 1;
      width: 25%;

      &:deep() {
        img {
          width: 100%;
          height: auto;
        }
      }
    }

    &__content {
      display: flex;
      flex: 1 0 0%;
      flex-direction: column;
      justify-content: flex-start;

      h6 {
        margin-bottom: 0.5rem;

        @include tablet-up {
          margin-bottom: 1rem;
        }

        strong {
          @include font-styles('Arial Narrow', 1.3rem, 600, normal, 1.7rem, 0, $dark-blue);
        }

        span {
          @include font-styles('Arial', 1.3rem, 400, normal, 1.7rem, 0, $dark-gray);
          display: none;

          @include desktop-up {
            display: inline;
          }
        }

        .c-search-list-item__link {
          @include font-styles('Arial Narrow', 1.3rem, 300, normal, 1.7rem, 0, $gray);
          text-transform: none;
        }
      }

      h2 {
        font-size: unset;
        line-height: 1;
        background: none;
        background-clip: unset;
        -webkit-background-clip: unset;
        -webkit-text-fill-color: unset;

        @include tablet-up {
          margin-bottom: 1.5rem;
        }

        a {
          @include font-styles('Arial', 1.6rem, 200, normal, 2rem, 0, $blue);

          @include large-desktop-up {
            font-size: 3rem;
            line-height: 3.2rem;
          }

          &:hover,
          &:active {
            text-decoration: underline;
          }
        }
      }

      p {
        @include font-styles('Arial', 1.4rem, 200, normal, 1.8rem, 0, $dark-gray);
        display: none;

        @include tablet-up {
          display: inline;
        }

        @include large-desktop-up {
          font-size: 2rem;
          line-height: 2.6rem;
        }
      }
    }
  }
}

nav {
  text-align: center;

  .pagination {
    margin: 0;

    .page-item {
      margin: 0;

      &:before {
        display: none;
      }

      .page-link {
        border: 0;
        border-radius: 0;
        @include font-styles('Arial', 2rem, 400, normal, 2.4rem, 0, $dark-gray);
        cursor: pointer;
        padding: 0.5rem 0.5rem;
        margin: 0 0.5rem;
        background-color: transparent;

        &:hover {
          background: none;
        }

        &:focus {
          outline: none;
        }

        &.disabled {
          pointer-events: none;
        }

        &.hide {
          display: none;
        }
      }

      &.active {
        .page-link {
          color: $blue;
          background-color: transparent;
          border-bottom: 3px solid $blue;
        }
      }
    }

    :first-child {
      .page-link {
        margin-right: 2rem;
      }
    }

    :last-child {
      .page-link {
        margin-left: 2rem;
      }
    }

    :first-child:not(.disabled) {
      .page-link {
        color: $blue;
      }
    }

    :last-child:not(.disabled) {
      .page-link {
        color: $blue;
      }
    }
  }
}

.c-search-paging {
  display: flex;
  justify-content: space-around;
}
</style>
